import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { BriefcaseIcon, BuildingIcon, ExternalLinkIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BusinessMaps } from './BusinessMaps';
export function BusinessInvestments() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true,
    margin: '-50px'
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [openLinks, setOpenLinks] = useState<boolean[]>([false, false]);
  const toggleLinks = (index: number) => {
    setOpenLinks(prev => prev.map((v, i) => i === index ? !v : v));
  };
  const cards = [{
    icon: BriefcaseIcon,
    titleEn: 'Trade & Investment Links',
    titleEs: 'Enlaces de Comercio e Inversión',
    descEn: 'U.S.A and international sources covering bilateral trade data, CAFTA-DR, market intelligence reports on digital transformation, infrastructure, 5G, and security technology, plus policy resources from the World Bank, IMF, IDB, and USTR.',
    descEs: 'EE.UU. y fuentes internacionales con datos de comercio bilateral, el tratado CAFTA-DR, informes de inteligencia de mercado sobre transformación digital, infraestructura, 5G y tecnología de seguridad, además de recursos de política del Banco Mundial, FMI, BID y USTR.',
    gradient: 'from-haa-skyblue to-haa-midblue',
    borderColor: 'border-haa-skyblue',
    accentColor: 'text-haa-skyblue',
    links: [{
      url: 'https://www.trade.gov/honduras',
      labelEn: 'U.S. Trade — Honduras',
      labelEs: 'Comercio de EE.UU. — Honduras'
    }, {
      url: 'https://www.census.gov/foreign-trade/balance/c2150.html#2024',
      labelEn: 'U.S.-Honduras Trade Balance (Census)',
      labelEs: 'Balanza Comercial EE.UU.-Honduras (Census)'
    }, {
      url: 'https://www.trade.gov/honduras-business-service-providers',
      labelEn: 'Honduras Business Service Providers Guide',
      labelEs: 'Guía de Proveedores de Servicios Empresariales en Honduras'
    }, {
      url: 'https://www.trade.gov/market-intelligence/honduras-digital-transformation',
      labelEn: 'Honduras Digital Transformation',
      labelEs: 'Transformación Digital de Honduras'
    }, {
      url: 'https://www.trade.gov/market-intelligence/honduras-infrastructure-road-program',
      labelEn: 'Honduras Infrastructure Road Program',
      labelEs: 'Programa de Infraestructura Vial de Honduras'
    }, {
      url: 'https://www.trade.gov/market-intelligence/honduras-5g-emerging-market',
      labelEn: 'Honduras 5G Emerging Market',
      labelEs: 'Mercado Emergente 5G de Honduras'
    }, {
      url: 'https://www.trade.gov/market-intelligence/honduras-security-technology',
      labelEn: 'Honduras Security Technology',
      labelEs: 'Tecnología de Seguridad de Honduras'
    }, {
      url: 'https://www.trade.gov/cafta',
      labelEn: 'CAFTA-DR Free Trade Agreement',
      labelEs: 'Tratado de Libre Comercio CAFTA-DR'
    }, {
      url: 'https://ustr.gov/countries-regions/western-hemisphere/honduras',
      labelEn: 'USTR — Honduras Trade Policy',
      labelEs: 'USTR — Política Comercial de Honduras'
    }, {
      url: 'https://www.worldbank.org/ext/en/country/honduras',
      labelEn: 'World Bank — Honduras',
      labelEs: 'Banco Mundial — Honduras'
    }, {
      url: 'https://www.imf.org/en/-/media/files/publications/cr/2025/english/1hndea2025001-print-pdf.pdf',
      labelEn: 'IMF Honduras Country Report 2025',
      labelEs: 'Informe del FMI sobre Honduras 2025'
    }, {
      url: 'https://idbinvest.org/en/countries/honduras',
      labelEn: 'IDB Invest — Honduras',
      labelEs: 'BID Invest — Honduras'
    }]
  }, {
    icon: BuildingIcon,
    titleEn: 'Honduras Investment Resources',
    titleEs: 'Recursos de Inversión de Honduras',
    descEn: 'Honduras investment resources: sector guides for national, tourism, agro, and infrastructure investment, the CNI investment portfolio and success stories, the Central Bank of Honduras, and the BCH Annual Report 2025.',
    descEs: 'Recursos de inversión de Honduras: guías sectoriales de inversión nacional, turismo, agricultura e infraestructura, el portafolio de inversiones y casos de éxito del CNI, el Banco Central de Honduras y la Memoria Anual BCH 2025.',
    gradient: 'from-haa-navy to-blue-950',
    borderColor: 'border-haa-navy',
    accentColor: 'text-haa-navy',
    links: [{
      url: 'https://cni.hn/',
      labelEn: 'CNI — National Investment Council',
      labelEs: 'CNI — Consejo Nacional de Inversiones'
    }, {
      url: 'https://cni.hn/portafolio-de-inversiones/',
      labelEn: 'CNI Investment Portfolio',
      labelEs: 'Portafolio de Inversiones CNI'
    }, {
      url: 'https://cni.hn/listado-de-casos-de-exito/',
      labelEn: 'CNI Success Stories',
      labelEs: 'Casos de Éxito CNI'
    }, {
      url: 'https://www.cohep.org/',
      labelEn: 'COHEP — Honduras Private Enterprise Council',
      labelEs: 'COHEP — Consejo Hondureño de la Empresa Privada'
    }, {
      url: 'https://www.cohep.org/informacion-economica/articulos-documentos/',
      labelEn: 'COHEP — Economic Information & Documents',
      labelEs: 'COHEP — Información Económica y Documentos'
    }, {
      url: 'https://amchamhonduras.org/',
      labelEn: 'AmCham Honduras',
      labelEs: 'AmCham Honduras'
    }, {
      url: 'https://online.flippingbook.com/view/972979540/',
      labelEn: 'Honduras National Investment Guide',
      labelEs: 'Guía Nacional de Inversión de Honduras'
    }, {
      url: 'https://www.bch.hn/EN',
      labelEn: 'Central Bank of Honduras',
      labelEs: 'Banco Central de Honduras'
    }, {
      url: 'https://www.bch.hn/estadisticos/GIE/LIBMemoria/Memoria%20Anual%202025.pdf',
      labelEn: 'BCH Annual Report 2025',
      labelEs: 'Memoria Anual BCH 2025'
    }, {
      url: 'https://sde.gob.hn/',
      labelEn: 'Secretaría de Desarrollo Económico (SDE)',
      labelEs: 'Secretaría de Desarrollo Económico (SDE)'
    }, {
      url: 'https://online.flippingbook.com/view/995450799/',
      labelEn: 'Honduras Infrastructure Investment Guide',
      labelEs: 'Guía de Inversión en Infraestructura de Honduras'
    }, {
      url: 'https://online.flippingbook.com/view/711329386/',
      labelEn: 'Honduras Agro Investment Guide',
      labelEs: 'Guía de Inversión Agrícola de Honduras'
    }, {
      url: 'https://online.flippingbook.com/view/656142784/',
      labelEn: 'Honduras Tourism Investment Guide',
      labelEs: 'Guía de Inversión Turística de Honduras'
    }, {
      url: 'https://www.aduanas.gob.hn/',
      labelEn: 'Administración Aduanera de Honduras',
      labelEs: 'Administración Aduanera de Honduras'
    }, {
      url: 'https://www.cnbs.gob.hn/',
      labelEn: 'Comisión Nacional de Bancos y Seguros (CNBS)',
      labelEs: 'Comisión Nacional de Bancos y Seguros (CNBS)'
    }, {
      url: 'https://arsa.gob.hn/',
      labelEn: 'Agencia de Regulación Sanitaria (ARSA)',
      labelEs: 'Agencia de Regulación Sanitaria (ARSA)'
    }, {
      url: 'https://sag.gob.hn/',
      labelEn: 'Secretaría de Agricultura y Ganadería (SAG)',
      labelEs: 'Secretaría de Agricultura y Ganadería (SAG)'
    }, {
      url: 'https://ccichonduras.org/',
      labelEn: 'Cámara de Comercio e Industrias de Cortés (CCIC)',
      labelEs: 'Cámara de Comercio e Industrias de Cortés (CCIC)'
    }, {
      url: 'https://www.ccit.hn/',
      labelEn: 'Cámara de Comercio e Industria de Tegucigalpa (CCIT)',
      labelEs: 'Cámara de Comercio e Industria de Tegucigalpa (CCIT)'
    }]
  }];
  return <section id="business-investments" className="bg-gray-50 py-16 lg:py-24" aria-labelledby="business-heading" data-id="element-231">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-232">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-233">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-234">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-235">
              {isEs ? 'Oportunidades Económicas' : 'Economic Opportunities'}
            </span>
            <h2 id="business-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-236">
              {isEs ? 'Negocios e Inversiones' : 'Business & Investments'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-237">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-238" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-239" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-240" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-241">
              {isEs ? 'Estamos compilando activamente una lista de empresas hondureño-americanas que fortalecen comunidades en todo Estados Unidos. En la segunda fase, identificaremos empresas con sede en EE.UU. que operan en Honduras, apoyando empleos, expandiendo oportunidades económicas y ayudando a construir un Honduras más fuerte y unos Estados Unidos más fuertes.' : 'We are actively compiling a list of Honduran-American-owned businesses strengthening communities across the United States. In phase two, we will identify U.S.-based businesses operating in Honduras that support jobs, expand economic opportunity, and help build a stronger Honduras and a stronger United States.'}
            </p>
          </motion.div>
        </div>

        {/* Interactive Maps Section */}
        <BusinessMaps data-id="element-242" />

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16" data-id="element-243">
          {cards.map((card, index) => {
          const Icon = card.icon;
          const isOpen = openLinks[index];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} animate={titleInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${card.borderColor} group hover:shadow-xl transition-all duration-300 flex flex-col`} data-id="element-244">
                <div className="p-6 sm:p-8 flex flex-col h-full" data-id="element-245">
                  <div className="flex items-center gap-4 mb-5" data-id="element-246">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300`} data-id="element-247">
                      <Icon className="w-7 h-7 text-white" data-id="element-248" />
                    </div>
                    <h3 className={`font-bebas text-3xl tracking-wider leading-tight ${card.accentColor}`} data-id="element-249">
                      {isEs ? card.titleEs : card.titleEn}
                    </h3>
                  </div>

                  <p className="font-nunito text-gray-600 text-base leading-relaxed mb-6 flex-grow whitespace-pre-line" data-id="element-250">
                    {(isEs ? card.descEs : card.descEn).split(/(https?:\/\/[^\s]+|[\w.-]+@[\w.-]+\.\w+)/g).map((part, i) => {
                  if (part.match(/^https?:\/\//)) {
                    return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-haa-blue underline hover:opacity-80 break-all" data-id="element-251">
                              {part}
                            </a>;
                  }
                  if (part.match(/^[\w.-]+@[\w.-]+\.\w+$/)) {
                    return <a key={i} href={`mailto:${part}`} className="text-haa-blue underline hover:opacity-80" data-id="element-252">
                              {part}
                            </a>;
                  }
                  return part;
                })}
                  </p>

                  <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-253">
                    <div data-id="element-254">
                      {/* Toggle button */}
                      <button onClick={() => toggleLinks(index)} className={`w-full flex items-center justify-between font-nunito font-bold text-sm ${card.accentColor} hover:opacity-80 transition-opacity py-1`} aria-expanded={isOpen} data-id="element-255">
                        <span data-id="element-256">
                          {isOpen ? isEs ? 'Ocultar enlaces' : 'Hide links' : isEs ? `Ver todos los enlaces (${card.links.length})` : `View all links (${card.links.length})`}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-257" />
                      </button>

                      {/* Cascading links */}
                      <AnimatePresence data-id="element-258">
                        {isOpen && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.35,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-259">
                            <div className="flex flex-col gap-1 pt-3" data-id="element-260">
                              {card.links.map((l, li) => <motion.a key={li} href={l.url} target="_blank" rel="noopener noreferrer" initial={{
                          opacity: 0,
                          x: -8
                        }} animate={{
                          opacity: 1,
                          x: 0
                        }} transition={{
                          duration: 0.25,
                          delay: li * 0.04,
                          ease: [0.16, 1, 0.3, 1]
                        }} className={`inline-flex items-center gap-2 font-nunito font-bold text-sm ${card.accentColor} hover:opacity-80 transition-opacity py-1 border-b border-gray-50 last:border-0`} data-id="element-261">
                                  {isEs ? l.labelEs : l.labelEn}
                                  <ExternalLinkIcon className="w-4 h-4 flex-shrink-0 ml-auto" data-id="element-262" />
                                </motion.a>)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}